import { Controller } from "@hotwired/stimulus";

type FragmentEvent = CustomEvent<{ target: HTMLElement }>;

export default class extends Controller<HTMLElement> {
  static targets = ["error", "editor", "uploadInput"];
  static values = { uploadFailed: String, singleFile: String };
  declare readonly errorTarget: HTMLElement;
  declare readonly uploadFailedValue: string;
  declare readonly singleFileValue: string;
  declare readonly editorTarget: HTMLElement;
  declare readonly uploadInputTarget: HTMLInputElement;
  declare readonly hasUploadInputTarget: boolean;

  private documentIds: Set<string> | null = null;
  private uploading = false;

  chooseFile(): void {
    if (this.uploading) return;
    this.uploadInputTarget.value = "";
    this.uploadInputTarget.click();
  }

  upload(): void {
    if (this.uploading || !this.uploadInputTarget.files?.length) return;
    this.uploading = true;
    this.uploadInputTarget.form?.requestSubmit();
  }

  dragOver(event: DragEvent): void {
    if (!event.dataTransfer?.types.includes("Files")) return;
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = this.hasUploadInputTarget && !this.uploading ? "copy" : "none";
    if (this.hasUploadInputTarget && !this.uploading) this.editorTarget.dataset.dragOver = "true";
  }

  dragLeave(event: DragEvent): void {
    if (event.relatedTarget instanceof Node && this.editorTarget.contains(event.relatedTarget)) return;
    delete this.editorTarget.dataset.dragOver;
  }

  drop(event: DragEvent): void {
    if (!event.dataTransfer?.types.includes("Files")) return;
    event.preventDefault();
    event.stopPropagation();
    delete this.editorTarget.dataset.dragOver;
    if (!this.hasUploadInputTarget || this.uploading) return;
    if (event.dataTransfer.files.length !== 1) {
      this.errorTarget.textContent = this.singleFileValue;
      return;
    }
    this.uploadInputTarget.files = event.dataTransfer.files;
    this.upload();
  }

  beforeRequest(event: FragmentEvent): void {
    if (event.detail.target.id !== "procedure-documents") return;
    this.errorTarget.textContent = "";
    this.documentIds = new Set(this.documents().map(document => document.id));
  }

  afterSwap(event: FragmentEvent): void {
    if (event.detail.target.id === "organization-procedures") {
      // Let Stimulus connect newly inserted editors, then prepare previously
      // hidden ones before the browser paints the new category.
      queueMicrotask(() => {
        if (!this.element.isConnected) return;
        for (const editor of this.element.querySelectorAll<HTMLElement>('[data-controller~="markdown-editor"]')) {
          if (editor.getClientRects().length > 0) editor.dispatchEvent(new Event("markdown-editor:open"));
        }
        const content = this.element.querySelector<HTMLElement>("#procedure-activity-content");
        if (!content) return;
        const top = content.getBoundingClientRect().top;
        if (top < 0 || top >= window.innerHeight) content.scrollIntoView({ block: "start", behavior: "instant" });
      });
      return;
    }
    if (event.detail.target.id !== "procedure-documents" || !this.documentIds) return;
    const previous = this.documentIds;
    this.documentIds = null;
    this.uploading = false;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    for (const document of this.documents()) {
      if (!previous.has(document.id)) {
        document.animate(
          [{ opacity: 0, transform: "translateY(6px)" }, { opacity: 1, transform: "translateY(0)" }],
          { duration: 280, easing: "ease-out" },
        );
      }
    }
  }

  failed(event: FragmentEvent): void {
    if (event.detail.target.id !== "procedure-documents") return;
    this.documentIds = null;
    this.uploading = false;
    this.errorTarget.textContent = this.uploadFailedValue;
  }

  private documents(): HTMLElement[] {
    return Array.from(this.element.querySelectorAll<HTMLElement>('[data-testid="agency-procedure-document"]'));
  }
}
